import apiClient from '../../shared/services/apiClient';

/**
 * Registrar Service (User Version)
 *
 * Handles user-level domain registration and contact template management.
 */

const registrarService = {
    // ==========================================
    // Contact Template Management
    // ==========================================

    /**
     * Get contact templates
     * @returns {Promise} List of contact templates
     */
    getContactTemplates: async () => {
        try {
            const response = await apiClient.get('/api/user/domain-contacts');
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Get single contact template
     * @param {number} id - Template ID
     * @returns {Promise} Template details
     */
    getContactTemplate: async (id) => {
        try {
            const response = await apiClient.get('/api/user/domain-contacts/get', {
                params: { id }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Get default contact template
     * @returns {Promise} Default template
     */
    getDefaultContactTemplate: async () => {
        try {
            const response = await apiClient.get('/api/user/domain-contacts/default');
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Create contact template
     * @param {Object} data - Template data
     * @returns {Promise} Creation result
     */
    createContactTemplate: async (data) => {
        try {
            const response = await apiClient.post('/api/user/domain-contacts/create', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Update contact template
     * @param {Object} data - Template data (including id, extension_fields optional)
     * @returns {Promise} Update result
     */
    updateContactTemplate: async (data) => {
        try {
            const response = await apiClient.post('/api/user/domain-contacts/update', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Delete contact template
     * @param {number} id - Template ID
     * @returns {Promise} Deletion result
     */
    deleteContactTemplate: async (id) => {
        try {
            const response = await apiClient.delete('/api/user/domain-contacts/delete', {
                data: { id }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Set default contact template
     * @param {number} id - Template ID
     * @returns {Promise} Operation result
     */
    setDefaultContactTemplate: async (id) => {
        try {
            const response = await apiClient.post('/api/user/domain-contacts/set-default', { id });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // ==========================================
    // TLD Additional Fields
    // ==========================================

    /**
     * Get list of TLDs that require extension fields
     * @returns {Promise} TLDs with field requirements, grouped by region
     */
    getSupportedTldsWithFields: async () => {
        try {
            const response = await apiClient.get('/api/user/domain-registration/supported-tlds-with-fields');
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Get additional fields configuration for a TLD
     * @param {string} tld - TLD (e.g., 'us', 'com.au')
     * @param {number} registrarId - Optional registrar ID
     * @param {string} operation - Operation type ('register' or 'transfer')
     * @returns {Promise} Fields configuration with contact_field_mapping
     */
    getAdditionalFields: async (tld, registrarId = null, operation = 'register') => {
        try {
            const params = { tld, operation };
            if (registrarId) {
                params.registrar_id = registrarId;
            }
            const response = await apiClient.get('/api/user/domain-registration/additional-fields', {
                params
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Get contact extension fields for a specific TLD
     * @param {number} contactId - Contact ID
     * @param {string} tld - TLD (e.g., 'us')
     * @returns {Promise} Extension fields data
     */
    getContactExtensionFields: async (contactId, tld) => {
        try {
            const response = await apiClient.get('/api/user/domain-contacts/extension-fields', {
                params: { id: contactId, tld }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Update contact extension fields for a specific TLD
     * @param {number} contactId - Contact ID
     * @param {string} tld - TLD
     * @param {Object} fields - Fields to update
     * @returns {Promise} Update result
     */
    updateContactExtensionFields: async (contactId, tld, fields) => {
        try {
            const response = await apiClient.post('/api/user/domain-contacts/extension-fields', {
                id: contactId,
                tld,
                fields
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    }
};

export default registrarService;
